import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';

import { IHallPass } from 'libs/domain';
import { ReplaySubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import scrollIntoView from 'scroll-into-view';

import { MgValidators } from '@app/src/app/input/validators';
import { RootService } from '@app/src/app/minimal/services/RootService';

import * as formUtils from '@shared/components/crud-form-base/crud-form-base.utils';
import { CrudFormBase } from '@shared/components/crud-form-base/crud-form-base.abstract';
import { FormSelectOption } from '@shared/components/form';
import {
  MODAL_OVERLAY_DATA,
  ModalOverlayPrimaryHeaderBackground,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@shared/components/modal-overlay';
import { SystemAlertModalService } from '@shared/components/system-alert-modal';
import { HallPassService } from '@shared/services/hall-pass';

import {
  HPM_HALL_PASS_EDIT_FORMGROUP,
  HPM_HALL_PASS_EDIT_FORM_FIELDS,
  HpmReportsMessages,
} from '../../hpm-reports.constants';
import { EditHallPassType } from '../../hpm-reports.types';

const createForm = formUtils.createForm(HPM_HALL_PASS_EDIT_FORMGROUP);
const setForm = formUtils.setForm(HPM_HALL_PASS_EDIT_FORMGROUP);
const getData = formUtils.getData(HPM_HALL_PASS_EDIT_FORMGROUP);

@Component({
  selector: 'mg-hpm-reports-history-edit',
  templateUrl: './hpm-reports-history-edit.component.html',
  styleUrls: ['./hpm-reports-history-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HpmReportsHistoryEditComponent extends CrudFormBase<EditHallPassType> implements OnDestroy, OnInit {
  private _destroyed = new ReplaySubject<void>(1);

  public readonly form = this._fb.group(createForm(), {
    validators: [
      MgValidators.DateTimeComparisonValidator(
        HPM_HALL_PASS_EDIT_FORM_FIELDS.START_DATE,
        HPM_HALL_PASS_EDIT_FORM_FIELDS.END_DATE,
        HPM_HALL_PASS_EDIT_FORM_FIELDS.START_TIME,
        HPM_HALL_PASS_EDIT_FORM_FIELDS.END_TIME,
      ),
    ],
  });
  public FORM_FIELDS = HPM_HALL_PASS_EDIT_FORM_FIELDS;
  public readonly MESSAGES = HpmReportsMessages;

  @Output() closeForm = new EventEmitter();

  public readonly hallPassTypesOptions$ = this._hpService.getHallPassTypes().pipe(
    takeUntil(this._destroyed),
    map(types =>
      types.map(type => ({
        label: type.name,
        value: type.id,
      })),
    ),
  );

  public readonly MODAL_CONFIG = {
    headerBg: ModalOverlayPrimaryHeaderBackground.PINK,
  };

  @ViewChild('crudForm', { static: false })
  crudForm?: ElementRef<HTMLElement>;

  constructor(
    @Inject(MODAL_OVERLAY_DATA)
    private _dialogData: IHallPass,
    private _fb: UntypedFormBuilder,
    public rootService: RootService,
    private _systemAlertModal: SystemAlertModalService,
    private _hpService: HallPassService,
    private _modalRef: ModalOverlayRef,
  ) {
    super({
      id: _dialogData?.id,
      get: async _data => _dialogData as IHallPass,
      create: async data => data as IHallPass,
      update: async data => {
        return this._hpService.updateHallPass(data as IHallPass);
      },
      delete: async data => data as IHallPass,
      onSetForm: data => {
        setForm(data, this.form);
      },
      onValidate: data => {
        formUtils.markNestedFormGroupAsDirty(this.form);
        return this.form.valid;
      },
      onSuccess: (type, data) => {
        this._modalRef.close(ModalOverlayServiceCloseEventType.SUBMIT, data);
      },
      onSubmit: data => {
        const mapped = getData(data, this.form);
        return mapped;
      },
      onShowLoader: promise => rootService.addLoadingPromise(promise),
      onCancel: async () => {
        this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
      },
      onDelete: async () => true,
      onFormStateChange: state => {
        if (state === 'invalid') {
          // scroll to top of form
          scrollIntoView(this.crudForm.nativeElement, {
            align: { top: 0 },
          });
        }
      },
    });
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  updateTypeName(option: FormSelectOption) {
    this._dialogData.hallPassType.name = option.label;
  }
}
